import { useEffect, useRef, type MutableRefObject } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';

const MIME_BY_FILE_TYPE: Record<string, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
};

/**
 * Fetches a PO file through a short-lived signed link and returns a blob: URL.
 * The blob is typed from purchase_order_files.file_type because storage may
 * hold a generic content type, which would make the iframe download the file.
 */
export async function fetchPoFileObjectUrl(poId: string, storagePath: string): Promise<string> {
  const { data } = await invokeFunction<{ signedUrl?: string; fileType?: string | null }>(supabase, 'serve-po-file', {
    body: { poId, path: storagePath },
  });
  if (!data?.signedUrl) throw new Error('No file link returned');

  const response = await fetch(data.signedUrl);
  if (!response.ok) throw new Error(`Failed to load file (${response.status})`);
  const type = MIME_BY_FILE_TYPE[data.fileType ?? ''] ?? 'application/octet-stream';
  return URL.createObjectURL(new Blob([await response.arrayBuffer()], { type }));
}

export function useRevokeObjectUrl(url: string | null | undefined): void {
  useEffect(() => () => {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  }, [url]);
}

export function useMountedRef(): MutableRefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}
